import { getIdToken } from "./auth";

type RequestOptions = {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  // Body crudo (texto CSV o binario .xlsx) con su Content-Type explícito.
  rawBody?: { body: string | ArrayBuffer; contentType: string };
  signal?: AbortSignal;
  headers?: Record<string, string>;
};

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

async function buildHeaders(
  contentType: string,
  forceRefresh: boolean,
  extra: Record<string, string> | undefined,
): Promise<HeadersInit> {
  const headers: Record<string, string> = { "Content-Type": contentType };
  const token = await getIdToken(forceRefresh);
  if (token) headers.Authorization = `Bearer ${token}`;
  // Caller-supplied headers cannot override Authorization or Content-Type — those
  // come from the auth + body contract and silently overriding them would mask bugs.
  if (extra) {
    for (const [key, value] of Object.entries(extra)) {
      const k = key.toLowerCase();
      if (k === "authorization" || k === "content-type") continue;
      headers[key] = value;
    }
  }
  return headers;
}

async function fetchOnce(
  path: string,
  method: string,
  body: unknown,
  rawBody: { body: string | ArrayBuffer; contentType: string } | undefined,
  signal: AbortSignal | undefined,
  forceRefresh: boolean,
  extraHeaders: Record<string, string> | undefined,
): Promise<Response> {
  const contentType = rawBody?.contentType ?? "application/json";
  const requestBody = rawBody
    ? rawBody.body
    : body !== undefined
      ? JSON.stringify(body)
      : undefined;
  return fetch(`/api${path}`, {
    method,
    headers: await buildHeaders(contentType, forceRefresh, extraHeaders),
    body: requestBody,
    signal,
  });
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, rawBody, signal, headers } = options;

  let res = await fetchOnce(path, method, body, rawBody, signal, false, headers);

  // Un único retry con token refrescado si el backend devuelve 401 — cubre
  // el caso de un token recién expirado que Firebase todavía cacheaba.
  if (res.status === 401) {
    res = await fetchOnce(path, method, body, rawBody, signal, true, headers);
  }

  if (!res.ok) {
    const payload = (await res.json().catch(() => ({}))) as {
      code?: string;
      error?: string;
      message?: string;
      details?: unknown;
    };
    throw new ApiError(
      res.status,
      payload.code ?? payload.error ?? "UNKNOWN_ERROR",
      payload.message ?? res.statusText,
      payload.details,
    );
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>(path, { method: "GET", signal }),
  post: <T>(path: string, body?: unknown, opts?: { headers?: Record<string, string> }) =>
    request<T>(path, { method: "POST", body, headers: opts?.headers }),
  postText: <T>(path: string, text: string, contentType: string) =>
    request<T>(path, { method: "POST", rawBody: { body: text, contentType } }),
  postRaw: <T>(path: string, body: string | ArrayBuffer, contentType: string) =>
    request<T>(path, { method: "POST", rawBody: { body, contentType } }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: "PATCH", body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: "PUT", body }),
  delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
};
